import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui-components/ui/dialog';
import { Button } from '../../../ui-components/ui/button';
import { Label } from '../../../ui-components/ui/label';
import { Input } from '../../../ui-components/ui/input';
import { Textarea } from '../../../ui-components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui-components/ui/select';
import { Calendar } from '../../../ui-components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui-components/ui/popover';
import { CalendarIcon, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils/utils';

interface DealOutcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: OutcomeData) => Promise<void>;
  dealId: string;
  dealTitle: string;
  requireNextStep?: boolean;
}

export interface OutcomeData {
  outcome: string;
  notes: string;
  nextAction?: {
    type: 'call' | 'email' | 'meeting' | 'task';
    description: string;
    date: Date;
    time: string;
  };
}

export const DealOutcomeModal: React.FC<DealOutcomeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  dealId,
  dealTitle,
  requireNextStep = true,
}) => {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [nextActionType, setNextActionType] = useState<'call' | 'email' | 'meeting' | 'task'>('call');
  const [nextActionDescription, setNextActionDescription] = useState('');
  const [nextActionDate, setNextActionDate] = useState<Date>();
  const [nextActionTime, setNextActionTime] = useState('10:00');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!outcome.trim()) {
      newErrors.outcome = 'Bitte geben Sie ein Ergebnis ein';
    }

    if (!notes.trim() || notes.length < 10) {
      newErrors.notes = 'Bitte geben Sie detaillierte Notizen ein (mind. 10 Zeichen)';
    }

    if (requireNextStep) {
      if (!nextActionDescription.trim()) {
        newErrors.nextAction = 'Bitte beschreiben Sie die nächste Aktion';
      }
      if (!nextActionDate) {
        newErrors.nextDate = 'Bitte wählen Sie ein Datum für die nächste Aktion';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }

    setIsSubmitting(true);
    try {
      const data: OutcomeData = {
        outcome,
        notes,
      };

      if (requireNextStep && nextActionDate) {
        data.nextAction = {
          type: nextActionType,
          description: nextActionDescription,
          date: nextActionDate,
          time: nextActionTime,
        };
      }

      await onSave(data);
      toast.success('Ergebnis gespeichert und nächste Aktion geplant');
      handleClose();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (requireNextStep && !nextActionDate) {
      toast.error('Sie müssen eine nächste Aktion planen, bevor Sie schließen können');
      return;
    }
    
    // Reset form
    setOutcome('');
    setNotes('');
    setNextActionDescription('');
    setNextActionDate(undefined);
    setNextActionTime('10:00');
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aktivität abschließen - {dealTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Outcome Field */}
          <div className="space-y-2">
            <Label htmlFor="outcome">
              Ergebnis <span className="text-red-500">*</span>
            </Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className={cn(errors.outcome && "border-red-500")}>
                <SelectValue placeholder="Wählen Sie das Ergebnis" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contacted">Kontaktiert - Gespräch geführt</SelectItem>
                <SelectItem value="no_answer">Nicht erreicht</SelectItem>
                <SelectItem value="callback_requested">Rückruf gewünscht</SelectItem>
                <SelectItem value="meeting_scheduled">Termin vereinbart</SelectItem>
                <SelectItem value="not_interested">Kein Interesse</SelectItem>
                <SelectItem value="proposal_sent">Angebot versendet</SelectItem>
                <SelectItem value="follow_up_needed">Nachfassen erforderlich</SelectItem>
              </SelectContent>
            </Select>
            {errors.outcome && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.outcome}
              </p>
            )}
          </div>

          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notizen <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Beschreiben Sie das Gespräch und wichtige Details..."
              className={cn("min-h-[100px]", errors.notes && "border-red-500")}
            />
            {errors.notes && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.notes}
              </p>
            )}
          </div>

          {/* Next Action Section */}
          {requireNextStep && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-medium flex items-center gap-2">
                Nächste Aktion <span className="text-red-500">*</span>
                <span className="text-sm text-gray-500">(erforderlich)</span>
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Aktionstyp</Label>
                  <Select value={nextActionType} onValueChange={(value: any) => setNextActionType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Anruf</SelectItem>
                      <SelectItem value="email">E-Mail</SelectItem>
                      <SelectItem value="meeting">Termin</SelectItem>
                      <SelectItem value="task">Aufgabe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Zeit</Label>
                  <Input
                    type="time"
                    value={nextActionTime}
                    onChange={(e) => setNextActionTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Datum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !nextActionDate && "text-muted-foreground",
                        errors.nextDate && "border-red-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {nextActionDate ? (
                        format(nextActionDate, 'PPP', { locale: de })
                      ) : (
                        <span>Datum wählen</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={nextActionDate}
                      onSelect={(date) => {
                        setNextActionDate(date);
                        if (errors.nextDate) {
                          setErrors({ ...errors, nextDate: '' });
                        }
                      }}
                      initialFocus
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      fromDate={new Date()}
                    />
                  </PopoverContent>
                </Popover>
                {errors.nextDate && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.nextDate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Input
                  value={nextActionDescription}
                  onChange={(e) => setNextActionDescription(e.target.value)}
                  placeholder="Was ist zu tun?"
                  className={cn(errors.nextAction && "border-red-500")}
                />
                {errors.nextAction && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.nextAction}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting || (requireNextStep && !nextActionDate)}
          >
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Speichern...' : 'Speichern & Nächste Aktion planen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
